import React, { useState, useEffect, useCallback } from 'react';

// Define the name of the cookie that will store consent status
const COOKIE_NAME = 'myWebsiteCookieConsent';

/**
 * Sets a cookie with a given name, value, and expiration days.
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value to store in the cookie.
 * @param {number} days - The number of days until the cookie expires.
 */
const setCookie = (name, value, days) => {
  let expires = '';
  if (days) {
    const date = new Date();
    // Set the expiration date for the cookie
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=${date.toUTCString()}`;
  }
  // Set the cookie. Path=/ ensures it's available across the entire site.
  // SameSite=Lax provides a good balance for security and usability.
  document.cookie = `${name}=${value}${expires}; path=/; SameSite=Lax`;
};

/**
 * Retrieves the value of a cookie by its name.
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string|null} The cookie's value, or null if not found.
 */
const getCookie = (name) => {
  // Create the string to look for (e.g., "myWebsiteCookieConsent=")
  const nameEQ = `${name}=`;
  // Split document.cookie into individual cookie strings
  const ca = document.cookie.split(';');
  // Iterate through the array of cookie strings
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    // Remove leading spaces from the cookie string
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    // If this cookie string begins with the name we want, return its value
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null; // Cookie not found
};

/**
 * CookieConsentProvider component manages the display and acceptance of a cookie consent banner.
 * It uses React state to control banner visibility and interacts with browser cookies.
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to render.
 * @param {function} props.onGtmConsentAccepted - Callback function to execute when consent is given.
 */
const CookieConsentWrapper = ({ children, onGtmConsentAccepted }) => {
  // State to control the visibility of the cookie banner
  const [showBanner, setShowBanner] = useState(false);

  // useCallback to memoize the handleAccept function, preventing unnecessary re-renders
  const handleAccept = useCallback(() => {
    // Set the consent cookie to 'true' with a 1-year expiration
    setCookie(COOKIE_NAME, 'true', 365);
    // Hide the banner immediately after acceptance
    setShowBanner(false);
    // Call the GTM consent callback if provided
    if (onGtmConsentAccepted) {
      onGtmConsentAccepted();
    }
  }, [onGtmConsentAccepted]); // Recreate if onGtmConsentAccepted changes

  // useEffect hook to run logic on component mount
  useEffect(() => {
    // Check if the consent cookie already exists and is set to 'true'
    const hasConsented = getCookie(COOKIE_NAME) === 'true';

    if (!hasConsented) {
      // If no consent cookie is found, show the banner
      setShowBanner(true);
    } else {
      // If consent was previously given, trigger the GTM load immediately
      if (onGtmConsentAccepted) {
        onGtmConsentAccepted();
      }
    }
  }, [onGtmConsentAccepted]); // Dependency array ensures this runs when onGtmConsentAccepted changes

  return (
    <>
      {children} {/* Always render children, so your main app content is visible */}

      {/* Conditionally render the cookie banner */}
      {showBanner && (
        <div
          // Bootstrap classes for fixed position at bottom, full width, flexbox layout, and padding
          className="fixed-bottom w-100 text-white p-3 d-flex flex-column flex-sm-row align-items-center justify-content-between animate-slideUp"
          style={{ backgroundColor: "#2B3748", zIndex: 1050, gap: "10px" }} // Custom background color and z-index for Bootstrap modals/navbars
        >
          <p
            className="text-center text-sm-left mb-2 mb-sm-0 flex-grow-1" // Bootstrap classes for text alignment and flex growth
            style={{ margin: "10px" }} // Margin for content
          >
            We value your privacy <br />We use cookies and other technologies to personalize your experience, perform marketing, and collect analytics. Learn more in our {' '}
            <a href="/privacy-policy" className="text-decoration-underline" style={{ color: "#87CEEB" }}>
              Privacy Policy
            </a>
            .
          </p>
          <button
            onClick={handleAccept}
            className="btn text-white font-weight-bold py-2 px-4 rounded" // Bootstrap button classes
            style={{
              backgroundColor: "#6cc04a", // Green button background
              fontSize: "14px", // Font size for the button text
              minWidth: "120px", // Ensure button has a minimum width
              border: "none", // No border for the button
              cursor: "pointer" // Pointer cursor on hover
            }}
          >
            Accept All Cookies
          </button>
        </div>
      )}

      {/* The corrected standard HTML style tag without the 'jsx' attribute */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slideUp {
          animation: slideUp 0.5s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default CookieConsentWrapper;
